<template>
  <div>
    <div class="d-flex flex-wrap align-center mb-2" style="gap: 12px">
      <v-select v-model="periodMode" :items="periodOptions" label="Период" outlined dense hide-details style="max-width: 200px" />
      <template v-if="periodMode === 'custom'">
        <v-text-field v-model="customFrom" type="date" label="С" outlined dense hide-details style="max-width: 170px" />
        <v-text-field v-model="customTo" type="date" label="По" outlined dense hide-details style="max-width: 170px" />
      </template>
      <v-select
        v-model="nomenclatureId"
        :items="testOptions"
        label="Тест"
        placeholder="Все тесты"
        clearable outlined dense hide-details
        style="max-width: 280px"
      />
      <v-select
        v-model="operationType"
        :items="typeOptions"
        label="Тип записи"
        placeholder="Любой"
        clearable outlined dense hide-details
        style="max-width: 200px"
      />
      <v-select
        v-model="owner"
        :items="ownerOptions"
        label="Кошелёк"
        placeholder="Буфер и менеджеры"
        clearable outlined dense hide-details
        style="max-width: 280px"
      />
    </div>
    <div class="caption grey--text text--darken-1 mb-4">
      Журнал показывает успешные операции буфера и текущих менеджеров компании. Внутренний перевод — одна строка.
    </div>

    <v-alert v-if="rangeError" type="warning" dense outlined>{{ rangeError }}</v-alert>
    <StateBlock v-else-if="error && error.status === 403" state="forbidden" />
    <StateBlock v-else-if="error" state="error" @retry="load" />
    <template v-else>
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        :options.sync="options"
        :server-items-length="count"
        :footer-props="{ 'items-per-page-options': [25, 50, 100], 'items-per-page-text': 'На странице:' }"
        item-key="id"
        show-expand
        single-expand
        disable-sort
        no-data-text="Операций за выбранный период нет"
        loading-text="Загрузка…"
      >
        <template v-slot:item.date="{ item }">
          <div>{{ formatDate(item.date) }}</div>
          <div v-if="item.time" class="caption grey--text">{{ item.time }}</div>
        </template>
        <template v-slot:item.title="{ item }">
          <span>{{ item.title }}</span>
        </template>
        <template v-slot:item.qty="{ item }">
          <span v-if="item.signedQty === null" class="grey--text text--darken-1">{{ item.qty }}</span>
          <span v-else :class="item.signedQty > 0 ? 'success--text' : 'error--text'">{{ item.signedQty > 0 ? '+' : '−' }}{{ item.qty }}</span>
        </template>
        <template v-slot:item.payer="{ item }">{{ participantLine(item.payer) }}</template>
        <template v-slot:item.recipient="{ item }">{{ participantLine(item.recipient) }}</template>
        <template v-slot:item.initiator="{ item }">{{ participantLine(item.initiator) }}</template>
        <template v-slot:item.purchaseId="{ item }">
          <template v-if="item.purchaseId">
            <router-link v-if="canOpenOrders" class="text-primary" :to="ordersLink">№{{ item.purchaseId }}</router-link>
            <span v-else>№{{ item.purchaseId }}</span>
            <div v-if="item.sourceLabel" class="caption grey--text">{{ item.sourceLabel }}</div>
          </template>
          <template v-else>
            <span v-if="item.sourceLabel">{{ item.sourceLabel }}</span>
            <span v-else>—</span>
          </template>
        </template>
        <template v-slot:expanded-item="{ headers: cols, item }">
          <td :colspan="cols.length" class="py-3 caption">
            <div v-if="item.comment" class="mb-1">Комментарий: {{ item.comment }}</div>
            <div>Тип записи: {{ item.typeLabel || '—' }}{{ item.typeCode != null ? ` (${item.typeCode})` : '' }}</div>
            <div>Номер записи: {{ item.id }}</div>
          </td>
        </template>
      </v-data-table>
    </template>
  </div>
</template>

<script>
import moment from 'moment';
import * as XLSX from 'xlsx';
import api from '@/common/api';
import StateBlock from '@/components/ui/StateBlock.vue';
import { OPERATION_TYPES, parseWalletTransactions } from '@/common/models/walletTransactions';
import { ADMIN } from '@/common/constants/userTypes';

const FORMAT = 'YYYY-MM-DD';
const MAX_DAYS = 3660;

// Журнал операций корп. кошелька: когда, что и кому начислено или списано. Данные готовит сервер,
// экран только фильтрует и показывает (направление определяет адаптер walletTransactions.js).
export default {
  name: 'ClientOperations',
  components: { StateBlock },
  props: {
    corpId: { type: [Number, String], required: true },
    company: { type: String, default: '' },
    // Сводка кошельков (из карточки корпа): из неё берём тесты и менеджеров для фильтров
    wallet: { type: Object, default: null },
    // Корп смотрит себя: ссылка на заказ ведёт в «Мои заказы»
    self: { type: Boolean, default: false },
  },
  data: () => ({
    periodMode: '30d',
    periodOptions: [
      { value: '7d', text: 'Последние 7 дней' },
      { value: '30d', text: 'Последние 30 дней' },
      { value: 'month', text: 'Текущий месяц' },
      { value: 'prev_month', text: 'Прошлый месяц' },
      { value: 'quarter', text: 'Текущий квартал' },
      { value: 'year', text: 'Последний год' },
      { value: 'custom', text: 'Свой диапазон' },
    ],
    customFrom: moment().subtract(29, 'days').format(FORMAT),
    customTo: moment().format(FORMAT),
    nomenclatureId: null,
    operationType: null,
    owner: null,
    typeOptions: OPERATION_TYPES,
    options: { page: 1, itemsPerPage: 25 },
    items: [],
    count: 0,
    loading: false,
    error: null,
    requestId: 0,
  }),
  computed: {
    headers() {
      return [
        { text: 'Дата', value: 'date', width: '110px' },
        { text: 'Операция', value: 'title' },
        { text: 'Тест', value: 'testName' },
        { text: 'Кол-во', value: 'qty', align: 'end' },
        { text: 'Откуда', value: 'payer' },
        { text: 'Куда', value: 'recipient' },
        { text: 'Кто инициировал', value: 'initiator' },
        { text: 'Заказ', value: 'purchaseId' },
        { text: '', value: 'data-table-expand' },
      ];
    },
    range() {
      const today = moment();
      switch (this.periodMode) {
        case '7d': return { from: today.clone().subtract(6, 'days').format(FORMAT), to: today.format(FORMAT) };
        case 'month': return { from: today.clone().startOf('month').format(FORMAT), to: today.format(FORMAT) };
        case 'prev_month': {
          const prev = today.clone().subtract(1, 'month');
          return { from: prev.clone().startOf('month').format(FORMAT), to: prev.clone().endOf('month').format(FORMAT) };
        }
        case 'quarter': return { from: today.clone().startOf('quarter').format(FORMAT), to: today.format(FORMAT) };
        case 'year': return { from: today.clone().subtract(364, 'days').format(FORMAT), to: today.format(FORMAT) };
        case 'custom': return { from: this.customFrom, to: this.customTo };
        default: return { from: today.clone().subtract(29, 'days').format(FORMAT), to: today.format(FORMAT) };
      }
    },
    rangeError() {
      const { from, to } = this.range;
      if (!from || !to) return 'Укажите обе даты периода.';
      if (moment(from).isAfter(moment(to))) return 'Дата начала позже даты окончания.';
      if (moment(to).diff(moment(from), 'days') > MAX_DAYS) return 'Период слишком длинный — не больше 10 лет.';
      return '';
    },
    testOptions() {
      const items = this.wallet ? this.wallet.items : [];
      return items.map((i) => ({ value: i.id, text: i.name })).sort((a, b) => a.text.localeCompare(b.text, 'ru'));
    },
    ownerOptions() {
      const managers = this.wallet ? this.wallet.managers : [];
      return [
        { value: 'buffer', text: 'Буфер компании' },
        ...managers.map((m) => ({ value: `manager:${m.id}`, text: `Менеджер: ${m.name}` })),
      ];
    },
    // Вкладка «Заказы» в карточке есть только у админа; корп открывает «Мои заказы»
    canOpenOrders() {
      const user = this.$store.getters['auth/user'];
      return this.self || (!!user && user.user_type === ADMIN);
    },
    ordersLink() {
      return this.self ? { name: 'my-orders' } : { name: 'clients-id', params: { id: this.corpId, tab: 'orders' } };
    },
    // Параметры запроса без пагинации — общие для таблицы и выгрузки
    filterParams() {
      const { from, to } = this.range;
      const params = { from_date: from, to_date: to };
      if (this.nomenclatureId != null) params.nomenclature_id = this.nomenclatureId;
      if (this.operationType != null) params.operation_type = this.operationType;
      if (this.owner) params.owner = this.owner;
      return params;
    },
    filterKey() {
      return JSON.stringify(this.filterParams);
    },
  },
  watch: {
    // Любой фильтр возвращает на первую страницу
    filterKey() {
      if (this.rangeError) {
        this.items = [];
        this.count = 0;
        return;
      }
      if (this.options.page !== 1) this.options = { ...this.options, page: 1 };
      else this.load();
    },
    // Тот же экран открыли для другого корпа: фильтры и данные прежней компании не переносим
    corpId() {
      // Сброс фильтров сам запустит загрузку через filterKey; грузим вручную только если фильтры уже были пустыми
      const filtersWereEmpty = this.nomenclatureId == null && this.operationType == null && !this.owner;
      this.nomenclatureId = null;
      this.operationType = null;
      this.owner = null;
      this.items = [];
      this.count = 0;
      if (this.options.page !== 1) this.options = { ...this.options, page: 1 };
      else if (filtersWereEmpty) this.load();
    },
    options(val, old) {
      if (!old || val.page !== old.page || val.itemsPerPage !== old.itemsPerPage) this.load();
    },
  },
  methods: {
    formatDate(value) {
      return value ? moment(value).format('DD.MM.YYYY') : '—';
    },
    // Имя и, если это не буфер, роль участника
    participantLine(p) {
      if (!p) return '—';
      return p.role && p.type !== 'candidate' ? `${p.title} · ${p.role}` : p.title;
    },
    async load() {
      if (this.rangeError) return;
      this.requestId += 1;
      const { requestId } = this;
      this.loading = true;
      this.error = null;
      try {
        const raw = await api.get(`/users/corp_clients/${this.corpId}/wallet-transactions/`, {
          params: { ...this.filterParams, page: this.options.page, page_size: this.options.itemsPerPage },
        });
        if (requestId !== this.requestId) return;
        const { items, count } = parseWalletTransactions(raw);
        this.items = items;
        this.count = count;
      } catch (err) {
        if (requestId !== this.requestId) return;
        this.items = [];
        this.count = 0;
        this.error = { status: (err && err.status) || null };
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    // Выгружаем весь отфильтрованный журнал, а не только открытую страницу
    async exportXlsx() {
      if (this.rangeError) return;
      try {
        const raw = await api.get(`/users/corp_clients/${this.corpId}/wallet-transactions/`, {
          params: { ...this.filterParams, page_size: -1 },
          timeout: 120000,
        });
        const { items } = parseWalletTransactions(raw);
        const { from, to } = this.range;
        const head = [
          [this.company || 'Корп. клиент'],
          [`Журнал операций: ${this.formatDate(from)} — ${this.formatDate(to)}`],
          [],
        ];
        const table = [
          ['Дата', 'Время', 'Операция', 'Тест', 'Кол-во', 'Откуда', 'Куда', 'Кто инициировал', 'Заказ №', 'Источник'],
          ...items.map((o) => [
            this.formatDate(o.date), o.time, o.title, o.testName, o.signedQty === null ? o.qty : o.signedQty,
            this.participantLine(o.payer), this.participantLine(o.recipient), this.participantLine(o.initiator),
            o.purchaseId || '', o.sourceLabel,
          ]),
        ];
        const ws = XLSX.utils.aoa_to_sheet([...head, ...table]);
        ws['!cols'] = [{ wch: 12 }, { wch: 8 }, { wch: 26 }, { wch: 32 }, { wch: 8 }, { wch: 30 }, { wch: 30 }, { wch: 30 }, { wch: 10 }, { wch: 22 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Журнал операций');
        XLSX.writeFile(wb, `Журнал операций${this.company ? ` — ${this.company}` : ''}.xlsx`.replace(/[\\/:*?"<>|]/g, ''));
      } catch (e) {
        this.$toast.error('Не удалось выгрузить журнал', '', { position: 'topRight' });
      }
    },
  },
  created() {
    this.load();
  },
};
</script>
